import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Spinner } from '@fluentui/react';
import AffinityToolTable from './AffinityToolTable';
import { useBoolean } from "@fluentui/react-hooks";
import ErrorComponent from '../../../../Util/ErrorComponent';
import MessageBarComponent from '../../../../Util/MessageBarComponent';
import FluentSelect from '../../../../Forms/FluentSelect';
import DeleteContentModal from '../../Series/Helpers/DeleteContentModal';

const MAX_SELECTABLE = 8;

const AffinityTools = ({ fieldName = 'tools', ...props }) => {
  const { setFieldValue, getFieldProps, values } = useFormikContext();
  const selectedTools = values[fieldName] || [];
  const [selectedTool, setSelectedTool] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAddTool, setShowAddTool] = useState(false);
  const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);

  // dataOptions.tools is the array of all available tools
  const allTools = values.dataOptions?.tools || [];
  const noEligibleData = !allTools || allTools.length === 0;
  const loading = useMemo(() => noEligibleData, [noEligibleData]);

  const handleRemoveTool = (item) => {
    if (!item || (!item.id && !item.toolId)) return;
    const updated = selectedTools.filter(c => c.toolId !== item.toolId);
    // Reassign order to be contiguous
    const reOrdered = updated.map((tool, idx) => ({ ...tool, order: idx + 1 }));
    setFieldValue(fieldName, reOrdered);
    setShowSuccess(true);
  };

  const mapNewContent = (id) => {
    if (!id) return;
    const item = allTools.find(x => x.toolId === id.value);
    if (selectedTools.some(c => c.toolId === item.toolId)) return; // Prevent duplicates
    item.order = selectedTools.length + 1; // Assign order based on current length
    const newlist = [...selectedTools, item];
    if (newlist.length > MAX_SELECTABLE) {
        setShowError(true);
        setErrorMsg(`You can only select up to ${MAX_SELECTABLE} tools.`);
        return;
    }
    setFieldValue(fieldName, newlist);
    setShowAddTool(false);
    setShowSuccess(true);
  };

  if (loading) return <Spinner className="absolute-center" label="Loading Tools..." />;

  return (
    <>
      <MessageBarComponent
        message="Tools updated. Remember to save."
        showMessage={showSuccess}
        setShowMessage={setShowSuccess}
      />
      <ErrorComponent
        errorMessage={errorMsg}
        showError={showError}
        setShowError={setShowError}
      />
      {showAddTool && (
        <div className="max-800">
          <FluentSelect
            options={allTools.map(c => ({ value: c.toolId, label: `${c.toolId} - ${c.name}` }))}
            onChange={mapNewContent}
            label='Eligible Tools'
            component={FluentSelect}
          />
          <label className="preview-upload-help">Search for tools by Name or ID. Then select to add.</label>
        </div>
      )}
      <AffinityToolTable
        content={selectedTools}
        selectedContent={selectedTool}
        setSelectedContent={setSelectedTool}
        deleteOnClick={handleRemoveTool}
        maxSelectable={MAX_SELECTABLE}
        showAddContent={showAddTool}
        loading={loading}
        createOnClick={() => setShowAddTool(true)}
      />
      <DeleteContentModal
        showModal={showDeleteModal}
        toggleShowModal={toggleDeleteModal}
        handleDelete={() => handleRemoveTool(selectedTool)}
        setErrorMsg={setErrorMsg}
        setShowError={setShowError}
        setShowSuccess={setShowSuccess}
        subText='Are you sure you want to delete this tool from the group?'
        />
    </>
  );
};

export default AffinityTools;
